/**
 * Typography Pro's view of the shared outline-font engine (`src/shared/curveText/fontEngine`).
 *
 * This file wires in fontkit plus the faces the addon bundles (Roboto, Arimo as Arial), and maps
 * design items (including badge regions) onto face requests.
 */
import { create } from 'fontkit';
import type { TypographyDesignItem } from './types';
import { normalizeBadge } from './config';
import {
  clearFaceCache,
  configureCurveFonts,
  coverageKey,
  ensureFace,
  faceSupport,
  getFace,
  normalizeStyle,
  normalizeWeight,
  primeFace,
  type FaceRequest,
  type FaceStyle,
  type FaceSupport,
  type FontkitFont,
} from '@shared/curveText/fontEngine';

export { ensurePreviewFontCss, fontSupportsText, loadFontCatalog } from '@shared/curveText/fontEngine';
export type { CatalogFont, FontkitBBox, FontkitFont, FontkitPath } from '@shared/curveText/fontEngine';

function familyOf(item: TypographyDesignItem): string {
  return String(item.fontFamily ?? '').replace(/^['"]|['"].*$/g, '').trim().toLowerCase();
}

export function bundledRobotoWeight(item: TypographyDesignItem): '400'|'700'|'900'|null {
  const weight = normalizeWeight(item.fontWeight);
  return familyOf(item) === 'roboto' && normalizeStyle(item.fontStyle) === 'normal' && ['400', '700', '900'].includes(weight) ? weight as '400'|'700'|'900' : null;
}

export function bundledArialFace(item: TypographyDesignItem): { weight: '400'|'700'; style: 'normal'|'italic' } | null {
  if (familyOf(item) !== 'arial') return null;
  return { weight: Number(normalizeWeight(item.fontWeight)) >= 600 ? '700' : '400', style: normalizeStyle(item.fontStyle) };
}

async function bundledFace(family: string, weight: string, style: FaceStyle): Promise<string> {
  const item = { fontFamily: family, fontWeight: weight, fontStyle: style } as TypographyDesignItem;
  const roboto = bundledRobotoWeight(item);
  if (roboto === '400') return (await import('@fontsource/roboto/files/roboto-latin-400-normal.woff2?inline')).default;
  if (roboto === '700') return (await import('@fontsource/roboto/files/roboto-latin-700-normal.woff2?inline')).default;
  if (roboto === '900') return (await import('@fontsource/roboto/files/roboto-latin-900-normal.woff2?inline')).default;
  const arial = bundledArialFace(item);
  if (!arial) return '';
  if (arial.weight === '400' && arial.style === 'normal') return (await import('@fontsource/arimo/files/arimo-latin-400-normal.woff2?inline')).default;
  if (arial.weight === '400' && arial.style === 'italic') return (await import('@fontsource/arimo/files/arimo-latin-400-italic.woff2?inline')).default;
  if (arial.weight === '700' && arial.style === 'normal') return (await import('@fontsource/arimo/files/arimo-latin-700-normal.woff2?inline')).default;
  return (await import('@fontsource/arimo/files/arimo-latin-700-italic.woff2?inline')).default;
}

configureCurveFonts({ create: (data) => create(data as never), bundled: bundledFace });

export function typographyCoverageText(item: TypographyDesignItem): string {
  if (!item.typographyBadge) return String(item.text ?? '');
  const badge = normalizeBadge(item.typographyBadge);
  return [badge.top.text, badge.bottom.text, ...(badge.center.enabled ? [badge.center.text] : [])].join(' ');
}

function faceRequest(item: TypographyDesignItem): FaceRequest {
  return {
    family: String(item.fontFamily ?? 'Arial'),
    weight: item.fontWeight ?? '400',
    style: item.fontStyle ?? 'normal',
    text: typographyCoverageText(item),
    explicitUrl: String(item.typographyFontUrl ?? ''),
  };
}

export function fontCoverageKey(item: TypographyDesignItem): string {
  return coverageKey(faceRequest(item));
}

/** The closest loaded face for the item (the exact face, or the fallback drawn in its place). */
export function getCachedFont(item: TypographyDesignItem): FontkitFont | null {
  return getFace(faceRequest(item));
}

/** Prime a parsed font from trusted bytes (used by preloaders and deterministic tests). */
export function primeFont(item: TypographyDesignItem, data: Uint8Array): FontkitFont {
  return primeFace(faceRequest(item), data);
}

export async function ensureFont(item: TypographyDesignItem): Promise<FontkitFont | null> {
  return ensureFace(faceRequest(item));
}

export async function ensureItemFonts(item: TypographyDesignItem): Promise<boolean> {
  if (!item.typographyBadge) return Boolean(await ensureFont(item));
  const badge = normalizeBadge(item.typographyBadge);
  const regions = [badge.top, badge.bottom, ...(badge.center.enabled ? [badge.center] : [])];
  const fonts = await Promise.all(regions.map((region) => ensureFont({ ...item, fontFamily: region.fontFamily, fontWeight: region.fontWeight })));
  return fonts.every(Boolean);
}

/** Bold / italic the item's family really ships; null while that is being probed. */
export function itemFaceSupport(item: TypographyDesignItem): FaceSupport | null {
  return faceSupport(faceRequest(item));
}

export function clearFontCache(): void {
  clearFaceCache();
}
